import type { ProductRow, ProductVersion, UpdateProductBody } from '../../../services/billing';
import type { VersionChanges } from '../../../services/productVersionPlan';

export type ProductAssignmentRequest = Pick<UpdateProductBody, 'vendorId'>;

// A 200 is not proof the write landed. Only the fields the request actually
// carried are asserted, since an omitted field is meant to keep its value.
export function checkVendorAssignmentOutcome(
    requested: ProductAssignmentRequest,
    saved: ProductRow
): string | null {
    if (requested.vendorId !== undefined && requested.vendorId !== saved.vendorId) {
        return 'Saved, but the vendor did not change on the server. Refresh and check.';
    }
    return null;
}

export interface VersionMethodCheck {
    label: string;
    requestedOrderMethodId: string | null;
    savedOrderMethodId: string | null;
}

// The method that started this whole guard: a version's ordering method must
// be checked individually, not rolled up into the product.
export function checkVersionMethodOutcome(checks: VersionMethodCheck[]): string | null {
    const drifted = checks
        .filter((check) => check.requestedOrderMethodId !== check.savedOrderMethodId)
        .map((check) => check.label);
    if (drifted.length === 0) return null;
    return `Saved, but the ordering method did not change on the server for ${drifted.join(', ')}. Refresh and check.`;
}

// Update path: `saved` is the write results in the same order as
// `changes.update` then `changes.create` - see useSaveProductVersions.
export function buildUpdateVersionMethodChecks(
    changes: VersionChanges,
    saved: ProductVersion[]
): VersionMethodCheck[] {
    const requests = [
        ...changes.update.map((change) => ({
            label: change.body.label ?? change.currentLabel,
            requestedOrderMethodId: change.body.orderMethodId ?? null
        })),
        ...changes.create.map((body) => ({
            label: body.label,
            requestedOrderMethodId: body.orderMethodId
        }))
    ];
    return requests.map((request, index) => ({
        ...request,
        savedOrderMethodId: saved[index]?.orderMethod?.id ?? null
    }));
}

// Create path: the server assigns fresh ids, so requested rows are matched to
// the response by label - validateProductForm already requires them unique.
export function buildCreateVersionMethodChecks(
    requestedVersions: { label: string; orderMethodId: string | null }[],
    savedVersions: ProductVersion[]
): VersionMethodCheck[] {
    return requestedVersions.map((request) => ({
        label: request.label,
        requestedOrderMethodId: request.orderMethodId,
        savedOrderMethodId:
            savedVersions.find((version) => version.label === request.label)?.orderMethod?.id ??
            null
    }));
}
